import { HttpClient } from '@angular/common/http';
import { Component, Inject, Input, OnInit, Self, ViewChild, AfterViewInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';
import { Utils } from '../../../../utils/utils';
import { DefaultValuesService } from '../../../../services/defaultvalues.service';
import { DestroyService } from '../../../../services/destroyservice';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'sfwversionconfiggeneration',
    templateUrl: './sfwversionconfiggeneration.component.html',
    styleUrls: [
        '../../../../style/new-generic-styles.component.less',
        '../../../../style/tables/table-add-butt-header-orange.less',
        '../manufacturingsettings.component.less',
        './sfwversionconfiggeneration.component.less'
    ],
    providers: [DestroyService],
    standalone: false
})
export class SfwVersionConfigGenerationComponent implements OnInit, AfterViewInit {
    @Input() availableCenterProducts: MFGCenterProductItem[] = [];
    @Input() availableControllerSoftwareReleases: EMTControllerSoftwareReleaseItem[] = [];
    @Input() availableCenters: MfgCenter[] = [];
    public dataSource = new MatTableDataSource<any>();
    SVCGForm!: FormGroup;
    @ViewChild(MatTable) objTable!: MatTable<any>;
    @ViewChild(MatSort) sort!: MatSort;
    originalSfwVersionConfigGen: SoftwareVersionConfiggenerationItem[] = [];
    modifiedSfwVersionConfigGen: SoftwareVersionConfiggenerationItem[] = [];
    newSfwVersionConfigGen: SoftwareVersionConfiggenerationItem[] = [];
    isCallSucceed: number = 0;
    subscriptionStage: any;

    columnsToDisplay = ["centerproduct", "controllersoftwareversion", "action"];

    constructor(
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
        private _formBuilder: FormBuilder,
        private toastrService: ToastrService,
        public defaults: DefaultValuesService,
        @Self() private readonly destroy$: DestroyService
    ) { }

    ngOnInit(): void {
        this.subscriptionStage = this.defaults.stageMessageChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => { this.getSoftwareVersion4ConfigGeneration() });
        this.SVCGForm = this._formBuilder.group({
            SVCGRows: this._formBuilder.array([])
        });
        this.getSoftwareVersion4ConfigGeneration();
    }

    ngAfterViewInit() {
        this.dataSource.sortData = (data, sort) => {
            if (!sort.active || sort.direction === '') {
                return data;
            }
            const isAsc = sort.direction === 'asc';
            return data.slice().sort((a, b) => {
                switch (sort.active) {
                    case 'centerproduct':
                        return (this.getCenterProductText(a.manufacturingCenterProductId) || '')
                            .localeCompare(this.getCenterProductText(b.manufacturingCenterProductId) || '') * (isAsc ? 1 : -1);
                    case 'controllersoftwareversion':
                        return (this.getControllerSoftwareText(a.controllerSoftwareVersionId) || '')
                            .localeCompare(this.getControllerSoftwareText(b.controllerSoftwareVersionId) || '') * (isAsc ? 1 : -1);
                    default:
                        return 0;
                }
            });
        };
        this.dataSource.sort = this.sort;
    }

    getCenterName(centerId: number) {
        return this.availableCenters.find(x => x.centerId === centerId)?.centerName ?? "---";
    }

    getCenterProductText(centerProductId: number) {
        let centerProd = this.availableCenterProducts.find(x => x.manufacturingCenterProductID === centerProductId);
        if (centerProd)
            return `${this.getCenterName(centerProd.mfgCenterId)} - ${centerProd.productName} - ${centerProd.productCode}`;
        else
            return "---";
    }

    getControllerSoftwareText(sfwVerId: number) {
        let sfwVer = this.availableControllerSoftwareReleases.find(x => x.controllerSoftwareVersionID === sfwVerId);
        if (sfwVer)
            return `${sfwVer.controllerSoftwareVersion}`;
        else
            return "---";
    }

    getReleaseName(sfwVerId: number) {
        return this.availableControllerSoftwareReleases.find(x => x.controllerSoftwareVersionID === sfwVerId)?.releaseName ?? '-';
    }

    getFilteredControllerSoftwareReleases() {
        return this.availableControllerSoftwareReleases.filter(r => 
            r.releaseName && r.releaseName.trim() !== ''
        );
    }

    updateControllerSoftwareVersionId(index: number, newValue: number) {
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        if (arr?.at(index)) {
            // Update the FormControl
            arr.at(index).get('controllerSoftwareVersionId')?.setValue(newValue);
            
            // Update the dataSource object directly (don't create a new array)
            if (this.dataSource.data[index]) {
                this.dataSource.data[index].controllerSoftwareVersionId = newValue;
            }
        }
    }

    updateManufacturingCenterProductId(index: number, newValue: number) {
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        if (arr?.at(index)) {
            // Update the FormControl
            arr.at(index).get('manufacturingCenterProductId')?.setValue(newValue);
            
            // Update the dataSource object directly (don't create a new array)
            if (this.dataSource.data[index]) {
                this.dataSource.data[index].manufacturingCenterProductId = newValue;
            }
        }
    }

    getSoftwareVersion4ConfigGeneration() {
        Utils.httpGetControllerSoftwareForConfigGeneration(
            this.http,
            this.baseUrl,
            this,
            (tthis: SfwVersionConfigGenerationComponent, result: SoftwareVersionConfigGeneration) => {
                tthis.originalSfwVersionConfigGen = result.softwareVersionConfigGenerationList;
                tthis.mapDataToForm(tthis.originalSfwVersionConfigGen);
            }
        );
    }

    mapDataToForm(data) {
        this.SVCGForm = this._formBuilder.group({
            SVCGRows: this._formBuilder.array(data.map(val => this._formBuilder.group({
                softwareVersionForConfigGenerationID: new FormControl(val.softwareVersionForConfigGenerationID),
                manufacturingCenterProductId: new FormControl(val.manufacturingCenterProductId ?? -1),
                controllerSoftwareVersionId: new FormControl(val.controllerSoftwareVersionId ?? -1),
                isEditable: new FormControl(true),
                isNewRow: new FormControl(false),
                action: new FormControl('existingRecord'),
            })))
        });
        this.dataSource.data = (this.SVCGForm.get('SVCGRows') as FormArray).value;
    }

    initSVCGForm(): FormGroup {
        return this._formBuilder.group({
            softwareVersionForConfigGenerationID: new FormControl(-1),
            manufacturingCenterProductId: new FormControl(-1),
            controllerSoftwareVersionId: new FormControl(-1),
            action: new FormControl('newRecord'),
            isEditable: new FormControl(false),
            isNewRow: new FormControl(true),
        });
    }

    addNewRow() {
        const control = this.SVCGForm.get('SVCGRows') as FormArray;
        control.insert(0, this.initSVCGForm());
        this.dataSource.data = control.value;
    }

    getSVCGRowFormGroup(id: number): FormGroup | null {
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        const index = arr.controls.findIndex(
            (group: any) => group.get('softwareVersionForConfigGenerationID')?.value === id
        );
        return index !== -1 ? arr.at(index) as FormGroup : null;
    }

    editRowByID(id: number) {
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        const idx = arr.controls.findIndex(
            (group: any) => group.get('softwareVersionForConfigGenerationID')?.value === id
        );
        if (idx !== -1) {
            arr.at(idx).get('isEditable')?.setValue(false);
            this.dataSource.data = arr.value;
        }
    }

    resetRowByID(id: number) {
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        const idx = arr.controls.findIndex(
            (group: any) => group.get('softwareVersionForConfigGenerationID')?.value === id
        );
        if (idx !== -1) {
            const isNewRow = arr.at(idx).get('isNewRow')?.value;
            if (isNewRow) {
                arr.removeAt(idx);
            } else {
                const original = this.originalSfwVersionConfigGen.find(
                    s => s.softwareVersionForConfigGenerationID === id
                );
                if (original) {
                    arr.at(idx).patchValue({
                        manufacturingCenterProductId: original.manufacturingCenterProductId ?? -1,
                        controllerSoftwareVersionId: original.controllerSoftwareVersionId ?? -1
                    });
                }
                arr.at(idx).get('isEditable')?.setValue(true);
            }
            this.dataSource.data = arr.value;
        }
    }

    deleteRow(i) {
        const control = this.SVCGForm.get('SVCGRows') as FormArray;
        control.removeAt(i);
        this.dataSource.data = control.value;
        this.refreshTable();
    }

    updateFormFeature(index) {
        var obj = this.SVCGForm.value.SVCGRows;
        var updated = this.originalSfwVersionConfigGen.find(s => s.softwareVersionForConfigGenerationID == this.SVCGForm.value.SVCGRows[index]?.softwareVersionForConfigGenerationID);
        obj[index].manufacturingCenterProductId = updated?.manufacturingCenterProductId ?? -1;
        obj[index].controllerSoftwareVersionId = updated?.controllerSoftwareVersionId ?? -1;
    }

    anyChange() {
        this.mapFormToData();
        return this.modifiedSfwVersionConfigGen?.length > 0 || this.newSfwVersionConfigGen?.length > 0;
    }

    mapFormToData() {
        // Read from FormArray (FormControls) to get the actual current values
        const arr = this.SVCGForm.get('SVCGRows') as FormArray;
        
        // Get values directly from FormControls
        const rows = arr.controls.map(control => control.value);
        
        this.modifiedSfwVersionConfigGen = rows.filter(row => {
            if (row.isNewRow) {
                return false;
            }
            
            // For existing rows, check if values have changed compared to original
            const original = this.originalSfwVersionConfigGen.find(
                orig => orig.softwareVersionForConfigGenerationID === row.softwareVersionForConfigGenerationID
            );
            
            if (!original) {
                console.log('No original found for row:', row);
                return false;
            }
            
            // Normalize values to handle null/undefined
            const currentMfgProductId = row.manufacturingCenterProductId ?? -1;
            const originalMfgProductId = original.manufacturingCenterProductId ?? -1;
            const currentControllerId = row.controllerSoftwareVersionId ?? -1;
            const originalControllerId = original.controllerSoftwareVersionId ?? -1;
            
            // Check if either field has changed
            const hasChanged = 
                currentMfgProductId !== originalMfgProductId ||
                currentControllerId !== originalControllerId;
            
            return hasChanged && 
                   currentMfgProductId !== -1 && 
                   currentControllerId !== -1;
        }).map(row => ({
            // Only send fields that the backend expects
            softwareVersionForConfigGenerationID: row.softwareVersionForConfigGenerationID,
            manufacturingCenterProductId: row.manufacturingCenterProductId,
            controllerSoftwareVersionId: row.controllerSoftwareVersionId,
            utcModifDate: row.utcModifDate,
            modifUser: row.modifUser
        }));
        
        this.newSfwVersionConfigGen = rows.filter(row => row.isNewRow
            && row.manufacturingCenterProductId !== -1
            && row.controllerSoftwareVersionId !== -1
        ).map(row => ({
            // Only send fields that the backend expects
            softwareVersionForConfigGenerationID: row.softwareVersionForConfigGenerationID,
            manufacturingCenterProductId: row.manufacturingCenterProductId,
            controllerSoftwareVersionId: row.controllerSoftwareVersionId,
            utcModifDate: row.utcModifDate,
            modifUser: row.modifUser
        }));
    }

    onSave() {
        if (this.anyChange()) {
            var configChanges = { 'modifiedConfig': this.modifiedSfwVersionConfigGen, 'newConfig': this.newSfwVersionConfigGen }
            Utils.httpUpdateControllerSoftwareConfigGeneration(
                this.http,
                this.baseUrl,
                configChanges,
                this,
                (tthis: SfwVersionConfigGenerationComponent, result: any) => {
                    tthis.getSoftwareVersion4ConfigGeneration();
                    tthis.funcNotify();
                }
            );
        }
    }

    funcNotify() {
        if (this.isCallSucceed === 1) {
            this.toastrService.success('Save request successfully sent!');
        }
        else if (this.isCallSucceed === -1) {
            this.toastrService.error('Save action could not be performed. Please, contact with administrator');
        }
    }

    refreshTable() {
        this.objTable.renderRows();
    }
}